import '@/src/api/client';
import { setBearerToken } from '@/src/api/auth-client';
import { isAuthOrAccessError } from '@/src/api/error-utils';
import type { TenantSummaryDto } from '@/src/api/generated';
import {
  tenantsControllerListTenants,
  tenantsControllerSelectTenant,
} from '@/src/api/generated/sdk.gen';
import { refreshStoredTenantSession } from './session-refresh';
import {
  getActiveTenantId,
  getAuthGeneration,
  getIdentityToken,
  isAuthGenerationCurrent,
  setActiveTenant,
  setTenantToken,
} from './token-storage';

export type TenantSelectionResult =
  | { status: 'selected' }
  | { status: 'blocked' }
  | { status: 'no-tenants' }
  | { status: 'missing-identity-token' }
  | { status: 'failed' };

async function recoverIdentitySession(generation: number) {
  try {
    const result = await refreshStoredTenantSession();
    return isAuthGenerationCurrent(generation) && result.status === 'refreshed';
  } catch {
    return false;
  }
}

export async function selectPreferredTenant(
  generation = getAuthGeneration(),
): Promise<TenantSelectionResult> {
  let canRecoverSession = true;

  while (true) {
    const token = getIdentityToken();

    if (!token) {
      return { status: 'missing-identity-token' };
    }

    const response = await tenantsControllerListTenants({ auth: token });

    if (!isAuthGenerationCurrent(generation)) return { status: 'failed' };

    if (response.error || !response.data) {
      if (isAuthOrAccessError(response.error) && canRecoverSession && (await recoverIdentitySession(generation))) {
        canRecoverSession = false;
        continue;
      }

      return { status: 'failed' };
    }

    const tenants = response.data.tenants;
    const activeTenantId = getActiveTenantId();
    const selectedTenant = tenants.find((tenant) => tenant.id === activeTenantId) ?? tenants[0];

    if (!selectedTenant) {
      return { status: 'no-tenants' };
    }

    return selectTenantById(selectedTenant.id, generation);
  }
}

export async function selectTenantById(
  tenantId: TenantSummaryDto['id'],
  generation = getAuthGeneration(),
  transaction: {
    canCommit?: () => boolean;
    onCommitted?: () => void;
  } = {},
): Promise<TenantSelectionResult> {
  let canRecoverSession = true;

  while (true) {
    const token = getIdentityToken();

    if (!token) {
      return { status: 'missing-identity-token' };
    }

    const response = await tenantsControllerSelectTenant({
      auth: token,
      body: { tenantId },
    });

    if (!isAuthGenerationCurrent(generation)) return { status: 'failed' };

    if (response.error || !response.data) {
      if (isAuthOrAccessError(response.error) && canRecoverSession && (await recoverIdentitySession(generation))) {
        canRecoverSession = false;
        continue;
      }

      return { status: 'failed' };
    }

    // The HTTP request can outlive the render that started it. Re-check the
    // live voice fence immediately before replacing tenant authority, then keep
    // storage, bearer installation, and optional navigation in one synchronous
    // commit so a newly answering/connected call cannot be crossed.
    if (transaction.canCommit && !transaction.canCommit()) {
      return { status: 'blocked' };
    }
    if (!setTenantToken(response.data.accessToken, generation)) return { status: 'failed' };
    if (!setActiveTenant(response.data.tenant, generation)) return { status: 'failed' };
    setBearerToken(response.data.accessToken);
    transaction.onCommitted?.();

    return { status: 'selected' };
  }
}
